import React, { createContext, useContext, useState, useEffect } from 'react';
import brandConfig from '../config/brandConfig';

const ProjectContext = createContext();

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};

export const ProjectProvider = ({ children, slug }) => {
  const [project, setProject] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLotData, setSelectedLotData] = useState(null);
  const [lotLoading, setLotLoading] = useState(false);
  const [lotError, setLotError] = useState(null);
  const [lotCache, setLotCache] = useState({}); // Cache for lot data

  // Function to get project slug from URL or prop
  const getProjectSlugFromUrl = () => {
    // If slug is provided as prop (from React Router), use it
    if (slug) {
      return slug;
    }
    // Fallback to query params for backward compatibility
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('project') || 'residencial-lima-norte-huertos'; // Default project slug
  };

  // Function to get lot ID from URL
  const getLotIdFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('lote');
  };

  // Function to update URL with lot ID
  const updateUrlWithLotId = (lotId) => {
    const url = new URL(window.location);
    if (lotId) {
      url.searchParams.set('lote', lotId);
    } else {
      url.searchParams.delete('lote');
    }
    window.history.replaceState({}, '', url);
  };

  // Get API URL from centralized config
  const apiUrl = brandConfig.apiUrl;

  // Function to fetch project data by slug
  const fetchProjectData = async (slug) => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${apiUrl}/projects/by-slug/${slug}`);
      if (response.ok) {
        const projectData = await response.json();
        setProject(projectData);
        // Store the project ID from the response
        setProjectId(projectData.id);
      } else {
        console.error('Failed to fetch project data');
        setError('Failed to fetch project data');
        // Set default project data if API fails
        setProject({
          name: 'Demo project',
          location: 'Ayacucho'
        });
      }
    } catch (error) {
      console.error('Error fetching project data:', error);
      setError(error.message);
      // Set default project data if API fails
      setProject({
        name: 'Demo project',
        location: 'Ayacucho'
      });
    } finally {
      setLoading(false);
    }
  };

  // Function to refresh project data
  const refreshProject = () => {
    const slug = getProjectSlugFromUrl();
    if (slug) {
      fetchProjectData(slug);
    }
  };

  // Function to fetch lot data with caching
  const fetchLotData = async (lotId) => {
    if (!lotId) {
      setSelectedLotData(null);
      updateUrlWithLotId(null); // Clear lot from URL
      return;
    }

    // Check cache first - if data is already in cache AND it's the same as current, don't update
    if (lotCache[lotId]) {
      // Only update if the data is different from current selected lot
      if (selectedLotData?.id !== lotId) {
        setSelectedLotData(lotCache[lotId]);
      }
      updateUrlWithLotId(lotId);
      return;
    }

    // Don't show loading if we're just switching lots - makes it smoother
    // Only show loading if we don't have any data yet
    if (!selectedLotData) {
      setLotLoading(true);
    }

    try {
      setLotError(null);

      // Update URL with lot ID
      updateUrlWithLotId(lotId);

      // Construir URL con project_id como query parameter
      const url = `${apiUrl}/lots/${lotId}/public${projectId ? `?project_id=${projectId}` : ''}`;

      const response = await fetch(url);
      if (response.ok) {
        const lotData = await response.json();

        // Update cache and selected lot data in one go using functional updates
        setLotCache(prev => ({
          ...prev,
          [lotId]: lotData
        }));
        setSelectedLotData(lotData);
      } else {
        console.error('Failed to fetch lot data:', response.status, response.statusText);
        setLotError(`Failed to fetch lot data: ${response.status} ${response.statusText}`);
        setSelectedLotData(null);
      }
    } catch (error) {
      console.error('Error fetching lot data:', error);
      setLotError(error.message);
      setSelectedLotData(null);
    } finally {
      setLotLoading(false);
    }
  };

  // Load project data on component mount or when slug changes
  useEffect(() => {
    const projectSlug = getProjectSlugFromUrl();
    if (projectSlug) {
      fetchProjectData(projectSlug);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Handle lot selection from URL
  useEffect(() => {
    const lotIdFromUrl = getLotIdFromUrl();
    if (lotIdFromUrl && projectId) {
      fetchLotData(lotIdFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const value = {
    project,
    projectId,
    loading,
    error,
    refreshProject,
    setProject,
    setProjectId,
    selectedLotData,
    lotLoading,
    lotError,
    fetchLotData,
    setSelectedLotData
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};
